<script setup>
/* global axios */
// Full-page WhatsApp template builder for 1msg inboxes: name/language/category, body with one example
// per variable, optional header (text / image / video / PDF with a sample upload), footer, buttons, and
// a live preview. Every rule runs in the browser (helper/whatsappTemplateRules.js) and again on the
// server (Smarticks::WhatsappTemplates::Rules); every reason Submit is disabled is listed.
// Plan: docs/smarticks/whatsapp-template-builder-plan.md.
import { computed, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import TemplatePreview from './TemplatePreview.vue';
import ButtonsSection from './ButtonsSection.vue';
import {
  BODY_MAX,
  EXAMPLE_MAX,
  FOOTER_MAX,
  HEADER_TEXT_MAX,
  LANGUAGES,
  bodyWarnings,
  validateBody,
  validateButtons,
  validateCategory,
  validateExamples,
  validateFooter,
  validateHeader,
  validateLanguage,
  validateName,
  variableToken,
  variableTokens,
} from 'dashboard/helper/whatsappTemplateRules';
import {
  ruleMessage,
  serverErrorMessage,
} from 'dashboard/helper/whatsappTemplateMessages';

const props = defineProps({
  accountId: { type: [String, Number], required: true },
  inboxId: { type: Number, required: true },
  templates: { type: Array, default: () => [] },
  // { mode: 'new' | 'duplicate' | 'fix', template?: <synced template> }
  start: { type: Object, default: () => ({ mode: 'new' }) },
});
const emit = defineEmits(['done', 'cancel']);
const { t, te } = useI18n();

const SAMPLE_ACCEPT = {
  IMAGE: 'image/jpeg,image/png',
  VIDEO: 'video/mp4,video/3gpp',
  DOCUMENT: 'application/pdf',
};
const SAMPLE_CAPS = {
  IMAGE: 5 * 1024 * 1024,
  VIDEO: 16 * 1024 * 1024,
  DOCUMENT: 16 * 1024 * 1024,
};

const component = (template, type) =>
  (template?.components || []).find(c => String(c.type).toUpperCase() === type);

const suggestName = base => {
  const taken = new Set(props.templates.map(tpl => tpl.name));
  for (let n = 2; n < 100; n += 1) {
    const candidate = `${base}_${n}`.slice(0, 512);
    if (!taken.has(candidate)) return candidate;
  }
  return base;
};

// Prefill from a synced template (duplicate / fix). A media header needs a NEW sample upload: the
// cached header_handle is a Meta CDN URL, not something we can resubmit.
const initialForm = () => {
  const source = props.start.template;
  const form = {
    name: '',
    language: 'he',
    category: 'UTILITY',
    body: '',
    examples: {},
    header: { format: 'NONE', text: '', example: '', sample_url: '' },
    footer: '',
    buttons: { order: 'qr_first', quick_replies: [], actions: [] },
  };
  if (!source) return form;

  form.name = suggestName(source.name);
  form.language = source.language || 'he';
  form.category = ['UTILITY', 'MARKETING'].includes(source.category)
    ? source.category
    : 'UTILITY';
  const body = component(source, 'BODY');
  form.body = body?.text || '';
  const bodyExamples = body?.example?.body_text?.[0] || [];
  variableTokens(form.body).forEach((n, index) => {
    form.examples[n] = bodyExamples[index] || '';
  });
  const header = component(source, 'HEADER');
  if (header) {
    const format = String(header.format || '').toUpperCase();
    form.header.format = ['TEXT', 'IMAGE', 'VIDEO', 'DOCUMENT'].includes(format)
      ? format
      : 'NONE';
    form.header.text = header.text || '';
    form.header.example = header.example?.header_text?.[0] || '';
  }
  form.footer = component(source, 'FOOTER')?.text || '';
  const buttons = component(source, 'BUTTONS')?.buttons || [];
  buttons.forEach(button => {
    const type = String(button.type).toUpperCase();
    if (type === 'QUICK_REPLY') {
      form.buttons.quick_replies.push({ text: button.text || '' });
    } else if (type === 'URL') {
      form.buttons.actions.push({
        type,
        text: button.text || '',
        url: button.url || '',
        example: (button.example || [])[0] || '',
      });
    } else if (type === 'PHONE_NUMBER') {
      form.buttons.actions.push({
        type,
        text: button.text || '',
        phone_number: button.phone_number || '',
      });
    } else if (type === 'COPY_CODE') {
      form.buttons.actions.push({ type, example: button.example || '' });
    }
  });
  const firstType = String(buttons[0]?.type || '').toUpperCase();
  form.buttons.order =
    firstType && firstType !== 'QUICK_REPLY' ? 'qr_last' : 'qr_first';
  return form;
};

const form = reactive(initialForm());
const pristine = JSON.stringify(form);
const submitting = ref(false);
const uploading = ref(false);
const sampleName = ref('');
const sampleError = ref('');
const showPreviewMobile = ref(false);
const confirmLeave = ref(false);
const serverError = ref('');
const bodyField = ref(null);
const fileInput = ref(null);

const isDirty = computed(() => JSON.stringify(form) !== pristine);

const languageOptions = LANGUAGES.map(code => {
  const key = `WA_TEMPLATES.BUILDER.LANG.${code}`;
  return { value: code, label: te(key) ? `${t(key)} (${code})` : code };
});
const headerOptions = computed(() =>
  ['NONE', 'TEXT', 'IMAGE', 'VIDEO', 'DOCUMENT'].map(value => ({
    value,
    label: t(`WA_TEMPLATES.BUILDER.HEADER.${value}`),
  }))
);

const bodyVariables = computed(() => {
  const error = validateBody(form.body);
  if (
    error &&
    ['invalid_placeholders', 'variables_out_of_order'].includes(error[0])
  ) {
    return [];
  }
  return variableTokens(form.body);
});
const bodyExamplesList = computed(() =>
  bodyVariables.value.map(n => form.examples[n] || '')
);
const headerHasVariable = computed(
  () => form.header.format === 'TEXT' && variableTokens(form.header.text).length
);
const hasButtons = computed(
  () =>
    form.buttons.quick_replies.length > 0 || form.buttons.actions.length > 0
);

const nameTaken = computed(() =>
  props.templates.some(
    tpl => tpl.name === form.name && tpl.language === form.language
  )
);
const nameOtherLanguage = computed(
  () =>
    !nameTaken.value && props.templates.some(tpl => tpl.name === form.name)
);

const headerForRules = computed(() => ({
  format: form.header.format || 'NONE',
  text: form.header.text,
  example: form.header.example || undefined,
  sample_url: form.header.sample_url || undefined,
}));
const buttonsForRules = computed(() =>
  hasButtons.value ? form.buttons : null
);

const blockers = computed(() => {
  const errors = [];
  const push = error => error && errors.push(ruleMessage(t, error));
  push(validateName(form.name));
  if (nameTaken.value) push(['name_taken']);
  push(validateLanguage(form.language));
  push(validateCategory(form.category));
  push(validateHeader(headerForRules.value));
  const bodyError = validateBody(form.body);
  push(bodyError);
  if (!bodyError) {
    push(validateExamples(bodyExamplesList.value, bodyVariables.value.length));
  }
  push(validateFooter(form.footer));
  push(validateButtons(buttonsForRules.value, form.category));
  return errors;
});
const warnings = computed(() =>
  bodyWarnings(form.body).map(warning => ruleMessage(t, warning))
);
const canSubmit = computed(
  () => blockers.value.length === 0 && !uploading.value
);

const previewButtons = computed(() => {
  const quickReplies = form.buttons.quick_replies.map(reply => ({
    type: 'QUICK_REPLY',
    label: reply.text,
  }));
  const actions = form.buttons.actions.map(action => ({
    type: action.type,
    label:
      action.type === 'COPY_CODE'
        ? t('WA_TEMPLATES.BUILDER.BUTTONS.COPY_PREVIEW')
        : action.text,
  }));
  return form.buttons.order === 'qr_last'
    ? [...actions, ...quickReplies]
    : [...quickReplies, ...actions];
});

const insertVariable = () => {
  const next = variableToken(variableTokens(form.body).length + 1);
  const textarea = bodyField.value?.$el?.querySelector('textarea');
  const position = textarea ? textarea.selectionStart : form.body.length;
  form.body = `${form.body.slice(0, position)}${next}${form.body.slice(position)}`;
};

// Renumber variables in first-appearance order (fixes {{1}} {{3}} or {{2}} before {{1}}).
const renumber = () => {
  const order = [];
  const pattern = /\{\{(\d+)\}\}/g;
  [...form.body.matchAll(pattern)].forEach(match => {
    if (!order.includes(match[1])) order.push(match[1]);
  });
  const oldExamples = { ...form.examples };
  form.body = form.body.replace(
    pattern,
    (_, n) => `{{${order.indexOf(n) + 1}}}`
  );
  form.examples = {};
  order.forEach((n, index) => {
    form.examples[String(index + 1)] = oldExamples[n] || '';
  });
};
const bodyError = computed(() => validateBody(form.body));
const canRenumber = computed(
  () => bodyError.value && bodyError.value[0] === 'variables_out_of_order'
);

const onHeaderFormat = format => {
  form.header.format = format || 'NONE';
  form.header.sample_url = '';
  sampleName.value = '';
  sampleError.value = '';
};
const pickFile = () => fileInput.value?.click();
const onFile = async event => {
  const file = event.target.files?.[0];
  event.target.value = '';
  if (!file) return;
  sampleError.value = '';
  const format = form.header.format;
  if (file.size > SAMPLE_CAPS[format]) {
    sampleError.value = ruleMessage(t, ['header_sample_too_large']);
    return;
  }
  uploading.value = true;
  try {
    const data = new FormData();
    data.append('inbox_id', props.inboxId);
    data.append('format_type', format);
    data.append('file', file);
    const { data: result } = await axios.post(
      `/api/v1/accounts/${props.accountId}/smarticks/onemsg/templates/header_sample`,
      data
    );
    form.header.sample_url = result.url;
    sampleName.value = file.name;
  } catch (error) {
    sampleError.value = serverErrorMessage(t, error);
  } finally {
    uploading.value = false;
  }
};
const removeSample = () => {
  form.header.sample_url = '';
  sampleName.value = '';
};

const payload = () => {
  const body = {
    inbox_id: props.inboxId,
    name: form.name,
    language: form.language,
    category: form.category,
    body: form.body,
    examples: bodyExamplesList.value.map(value => value.trim()),
  };
  if (form.header.format && form.header.format !== 'NONE') {
    body.header = { format: form.header.format };
    if (form.header.format === 'TEXT') {
      body.header.text = form.header.text;
      if (headerHasVariable.value) body.header.example = form.header.example;
    } else {
      body.header.sample_url = form.header.sample_url;
    }
  }
  if (form.footer.trim()) body.footer = form.footer;
  if (hasButtons.value) {
    body.buttons = {
      order: form.buttons.order,
      quick_replies: form.buttons.quick_replies.map(reply => ({
        text: reply.text,
      })),
      actions: form.buttons.actions,
    };
  }
  return body;
};

const submit = async () => {
  if (!canSubmit.value || submitting.value) return;
  submitting.value = true;
  serverError.value = '';
  try {
    const { data } = await axios.post(
      `/api/v1/accounts/${props.accountId}/smarticks/onemsg/templates`,
      payload()
    );
    if (data.created?.unconfirmed) {
      useAlert(t('WA_TEMPLATES.CREATE.UNCONFIRMED'));
    } else if (data.created?.status === 'rejected') {
      useAlert(t('WA_TEMPLATES.CREATE.REJECTED_AT_ONCE'));
    } else {
      useAlert(t('WA_TEMPLATES.BUILDER.CREATED'));
    }
    emit('done', { templates: data.templates || [], name: form.name });
  } catch (error) {
    // The builder stays open with everything the admin typed.
    serverError.value = serverErrorMessage(t, error);
  } finally {
    submitting.value = false;
  }
};

const leave = () => {
  if (isDirty.value && !confirmLeave.value) {
    confirmLeave.value = true;
    return;
  }
  emit('cancel');
};

const title = computed(
  () =>
    ({
      fix: t('WA_TEMPLATES.BUILDER.TITLE_FIX'),
      duplicate: t('WA_TEMPLATES.BUILDER.TITLE_DUPLICATE'),
    })[props.start.mode] || t('WA_TEMPLATES.BUILDER.TITLE_NEW')
);
const counter = (text, max) => `${[...(text || '')].length}/${max}`;
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex items-center gap-2">
      <NextButton link slate sm @click="leave">
        <Icon icon="i-lucide-chevron-left" class="size-4 rtl:scale-x-[-1]" />
        {{ t('WA_TEMPLATES.BUILDER.BACK') }}
      </NextButton>
    </div>
    <h2 class="text-lg font-medium text-n-slate-12">{{ title }}</h2>
    <p class="text-sm text-n-amber-11 -mt-2">
      {{ t('WA_TEMPLATES.CREATE.APPROVAL_NOTE') }}
    </p>

    <div
      v-if="confirmLeave"
      class="flex flex-wrap items-center gap-3 rounded-lg outline outline-1 -outline-offset-1 outline-n-weak p-3"
    >
      <span class="text-sm text-n-slate-12">
        {{ t('WA_TEMPLATES.BUILDER.LEAVE.TITLE') }}
        {{ t('WA_TEMPLATES.BUILDER.LEAVE.BODY') }}
      </span>
      <NextButton
        faded
        ruby
        sm
        :label="t('WA_TEMPLATES.BUILDER.LEAVE.CONFIRM')"
        @click="emit('cancel')"
      />
      <NextButton
        faded
        slate
        sm
        :label="t('WA_TEMPLATES.BUILDER.LEAVE.CANCEL')"
        @click="confirmLeave = false"
      />
    </div>

    <div class="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem] lg:items-start">
      <div class="min-w-0 flex flex-col gap-6 lg:col-start-1 lg:row-start-1">
        <!-- Name and language -->
        <section
          class="rounded-xl outline outline-1 -outline-offset-1 outline-n-weak bg-n-solid-1 p-4 flex flex-col gap-3"
        >
          <h3 class="text-sm font-medium text-n-slate-12">
            {{ t('WA_TEMPLATES.BUILDER.SECTION.BASICS') }}
          </h3>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div class="sm:col-span-2">
              <Input
                v-model="form.name"
                custom-input-class="[direction:ltr] font-inter"
                :label="t('WA_TEMPLATES.BUILDER.NAME.LABEL')"
                placeholder="order_update"
                :message="
                  nameOtherLanguage
                    ? t('WA_TEMPLATES.BUILDER.NAME.OTHER_LANGUAGE')
                    : t('WA_TEMPLATES.BUILDER.NAME.BLOCK_NOTE')
                "
              />
            </div>
            <div class="flex flex-col gap-1">
              <span class="text-sm font-medium text-n-slate-12">{{
                t('WA_TEMPLATES.FORM.LANGUAGE')
              }}</span>
              <ComboBox
                v-model="form.language"
                :options="languageOptions"
                class="[&>div>button]:bg-n-alpha-black2"
              />
            </div>
          </div>
        </section>

        <!-- Category -->
        <section
          class="rounded-xl outline outline-1 -outline-offset-1 outline-n-weak bg-n-solid-1 p-4 flex flex-col gap-3"
        >
          <h3 class="text-sm font-medium text-n-slate-12">
            {{ t('WA_TEMPLATES.BUILDER.CATEGORY.QUESTION') }}
          </h3>
          <label
            v-for="category in ['UTILITY', 'MARKETING']"
            :key="category"
            class="flex items-start gap-3 min-h-11 cursor-pointer"
          >
            <input
              v-model="form.category"
              type="radio"
              :value="category"
              class="mt-1"
            />
            <span class="flex flex-col">
              <span class="text-sm text-n-slate-12">{{
                t(`WA_TEMPLATES.CATEGORY.${category}`)
              }}</span>
              <span class="text-xs text-n-slate-11">{{
                t(`WA_TEMPLATES.BUILDER.CATEGORY.${category}_DESC`)
              }}</span>
            </span>
          </label>
          <p class="text-xs text-n-slate-11">
            {{ t('WA_TEMPLATES.BUILDER.CATEGORY.AFTER_SYNC') }}
          </p>
        </section>

        <!-- Body -->
        <section
          class="rounded-xl outline outline-1 -outline-offset-1 outline-n-weak bg-n-solid-1 p-4 flex flex-col gap-3"
        >
          <div class="flex flex-wrap items-center justify-between gap-2">
            <h3 class="text-sm font-medium text-n-slate-12">
              {{ t('WA_TEMPLATES.BUILDER.SECTION.BODY') }}
            </h3>
            <div class="flex gap-2">
              <NextButton
                v-if="canRenumber"
                ghost
                slate
                sm
                icon="i-lucide-list-ordered"
                :label="t('WA_TEMPLATES.BUILDER.BODY.RENUMBER')"
                @click="renumber"
              />
              <NextButton
                ghost
                slate
                sm
                icon="i-lucide-braces"
                :label="t('WA_TEMPLATES.BUILDER.BODY.INSERT_VARIABLE')"
                @click="insertVariable"
              />
            </div>
          </div>
          <TextArea
            ref="bodyField"
            v-model="form.body"
            dir="auto"
            show-character-count
            auto-height
            :max-length="BODY_MAX"
            min-height="6rem"
            :placeholder="t('WA_TEMPLATES.FORM.BODY_PLACEHOLDER')"
            :message="bodyError && form.body ? ruleMessage(t, bodyError) : ''"
            :message-type="bodyError && form.body ? 'error' : 'info'"
          />
          <p class="text-xs text-n-slate-11">
            {{ t('WA_TEMPLATES.FORM.BODY_HINT') }}
          </p>
          <div
            v-if="bodyVariables.length"
            class="flex flex-col gap-2 rounded-lg outline outline-1 -outline-offset-1 outline-n-weak p-3"
          >
            <p class="text-sm font-medium text-n-slate-12">
              {{ t('WA_TEMPLATES.FORM.EXAMPLES_TITLE') }}
            </p>
            <p class="text-xs text-n-slate-11">
              {{ t('WA_TEMPLATES.FORM.EXAMPLES_HINT') }}
            </p>
            <div
              class="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-2 items-center"
            >
              <template v-for="n in bodyVariables" :key="n">
                <bdi
                  dir="ltr"
                  class="font-mono text-xs px-1.5 py-0.5 rounded-md bg-n-slate-3 text-n-slate-12"
                  >{{ variableToken(n) }}</bdi
                >
                <input
                  v-model="form.examples[n]"
                  type="text"
                  dir="auto"
                  :maxlength="EXAMPLE_MAX"
                  :aria-label="t('WA_TEMPLATES.FORM.EXAMPLE_ARIA', { n })"
                  :placeholder="t('WA_TEMPLATES.FORM.EXAMPLE_PLACEHOLDER')"
                  class="w-full rounded-lg outline outline-1 -outline-offset-1 outline-n-weak px-3 py-2 text-sm text-n-slate-12 bg-n-alpha-black2"
                />
              </template>
            </div>
          </div>
        </section>

        <!-- Header -->
        <section
          class="rounded-xl outline outline-1 -outline-offset-1 outline-n-weak bg-n-solid-1 p-4 flex flex-col gap-3"
        >
          <h3 class="text-sm font-medium text-n-slate-12">
            {{ t('WA_TEMPLATES.BUILDER.SECTION.HEADER') }}
            <span class="text-xs font-normal text-n-slate-11">{{
              t('WA_TEMPLATES.BUILDER.SECTION.OPTIONAL')
            }}</span>
          </h3>
          <ComboBox
            :model-value="form.header.format"
            :options="headerOptions"
            class="[&>div>button]:bg-n-alpha-black2"
            @update:model-value="onHeaderFormat"
          />
          <template v-if="form.header.format === 'TEXT'">
            <Input
              v-model="form.header.text"
              :label="t('WA_TEMPLATES.BUILDER.HEADER.TEXT_LABEL')"
              :message="`${t('WA_TEMPLATES.BUILDER.HEADER.TEXT_HINT')} · ${counter(form.header.text, HEADER_TEXT_MAX)}`"
            />
            <Input
              v-if="headerHasVariable"
              v-model="form.header.example"
              :label="t('WA_TEMPLATES.BUILDER.HEADER.EXAMPLE_LABEL')"
              :placeholder="t('WA_TEMPLATES.FORM.EXAMPLE_PLACEHOLDER')"
            />
          </template>
          <template
            v-else-if="['IMAGE', 'VIDEO', 'DOCUMENT'].includes(form.header.format)"
          >
            <p class="text-xs text-n-slate-11">
              {{ t('WA_TEMPLATES.BUILDER.HEADER.SAMPLE_HINT') }}
            </p>
            <input
              ref="fileInput"
              type="file"
              class="hidden"
              :accept="SAMPLE_ACCEPT[form.header.format]"
              @change="onFile"
            />
            <NextButton
              :color="sampleError ? 'ruby' : 'slate'"
              :variant="sampleError ? 'outline' : 'faded'"
              :is-loading="uploading"
              class="!w-full !h-auto !justify-between !py-4"
              @click="pickFile"
            >
              <span class="flex items-center gap-3 min-w-0">
                <span
                  class="size-10 rounded-lg bg-n-slate-3 flex items-center justify-center shrink-0"
                >
                  <Icon
                    :icon="
                      {
                        IMAGE: 'i-lucide-image',
                        VIDEO: 'i-lucide-video',
                        DOCUMENT: 'i-lucide-file-text',
                      }[form.header.format]
                    "
                    class="size-5 text-n-slate-11"
                  />
                </span>
                <span class="flex flex-col items-start min-w-0">
                  <bdi
                    v-if="sampleName"
                    class="text-sm text-n-slate-12 truncate font-inter"
                    >{{ sampleName }}</bdi
                  >
                  <span v-else class="text-sm text-n-slate-12">{{
                    t('WA_TEMPLATES.BUILDER.HEADER.UPLOAD')
                  }}</span>
                  <span class="text-xs text-n-slate-11">{{
                    t(`WA_TEMPLATES.BUILDER.HEADER.LIMIT_${form.header.format}`)
                  }}</span>
                </span>
              </span>
              <Icon icon="i-lucide-upload" class="size-4 shrink-0" />
            </NextButton>
            <div v-if="sampleName" class="flex items-center gap-2">
              <span class="text-xs text-n-teal-11">{{
                t('WA_TEMPLATES.BUILDER.HEADER.UPLOADED')
              }}</span>
              <NextButton
                ghost
                slate
                sm
                icon="i-lucide-x"
                :label="t('WA_TEMPLATES.BUILDER.REMOVE_SECTION')"
                @click="removeSample"
              />
            </div>
            <p v-if="sampleError" class="text-xs text-n-ruby-11">
              {{ sampleError }}
            </p>
          </template>
        </section>

        <!-- Footer -->
        <section
          class="rounded-xl outline outline-1 -outline-offset-1 outline-n-weak bg-n-solid-1 p-4 flex flex-col gap-3"
        >
          <h3 class="text-sm font-medium text-n-slate-12">
            {{ t('WA_TEMPLATES.BUILDER.SECTION.FOOTER') }}
            <span class="text-xs font-normal text-n-slate-11">{{
              t('WA_TEMPLATES.BUILDER.SECTION.OPTIONAL')
            }}</span>
          </h3>
          <Input
            v-model="form.footer"
            :placeholder="t('WA_TEMPLATES.BUILDER.FOOTER.PLACEHOLDER')"
            :message="`${t('WA_TEMPLATES.BUILDER.FOOTER.HINT')} · ${counter(form.footer, FOOTER_MAX)}`"
          />
        </section>

        <!-- Buttons -->
        <section
          class="rounded-xl outline outline-1 -outline-offset-1 outline-n-weak bg-n-solid-1 p-4 flex flex-col gap-3"
        >
          <h3 class="text-sm font-medium text-n-slate-12">
            {{ t('WA_TEMPLATES.BUILDER.SECTION.BUTTONS') }}
            <span class="text-xs font-normal text-n-slate-11">{{
              t('WA_TEMPLATES.BUILDER.SECTION.OPTIONAL')
            }}</span>
          </h3>
          <ButtonsSection v-model="form.buttons" :category="form.category" />
        </section>
      </div>

      <!-- Preview -->
      <div
        class="lg:col-start-2 lg:row-start-1 lg:row-span-2 lg:sticky lg:top-4 lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto flex flex-col gap-2"
      >
        <NextButton
          class="lg:!hidden"
          ghost
          slate
          sm
          :icon="showPreviewMobile ? 'i-lucide-eye-off' : 'i-lucide-eye'"
          :label="
            showPreviewMobile
              ? t('WA_TEMPLATES.BUILDER.PREVIEW.HIDE')
              : t('WA_TEMPLATES.BUILDER.PREVIEW.SHOW')
          "
          @click="showPreviewMobile = !showPreviewMobile"
        />
        <div :class="showPreviewMobile ? 'block' : 'hidden lg:block'">
          <TemplatePreview
            :header="headerForRules"
            :body="form.body"
            :body-examples="form.examples"
            :footer="form.footer"
            :buttons="previewButtons"
            :sample-name="sampleName"
            :empty-body="t('WA_TEMPLATES.FORM.BODY_PLACEHOLDER')"
          >
            <template #caption>
              <p class="text-xs text-n-slate-11">
                {{ t('WA_TEMPLATES.BUILDER.PREVIEW.NOTE') }}
              </p>
            </template>
          </TemplatePreview>
        </div>
      </div>

      <!-- Actions -->
      <div
        class="flex flex-col gap-3 pt-4 border-t border-n-weak lg:col-start-1 lg:row-start-2"
      >
        <div
          v-if="serverError"
          class="rounded-lg outline outline-1 -outline-offset-1 outline-n-ruby-6 bg-n-ruby-2 p-3 text-sm text-n-ruby-11"
        >
          <span dir="auto">{{ serverError }}</span>
        </div>
        <div v-if="warnings.length" class="flex flex-col gap-1">
          <p class="text-xs font-medium text-n-amber-11">
            {{ t('WA_TEMPLATES.BUILDER.WARNINGS') }}
          </p>
          <ul class="text-xs text-n-amber-11 flex flex-col gap-1" dir="auto">
            <li v-for="(warning, index) in warnings" :key="index">
              {{ warning }}
            </li>
          </ul>
        </div>
        <div v-if="blockers.length" class="flex flex-col gap-1">
          <p class="text-xs font-medium text-n-ruby-11">
            {{ t('WA_TEMPLATES.BUILDER.BLOCKERS', blockers.length) }}
          </p>
          <ul class="text-xs text-n-ruby-11 flex flex-col gap-1" dir="auto">
            <li v-for="(blocker, index) in blockers" :key="index">
              {{ blocker }}
            </li>
          </ul>
        </div>
        <div class="flex flex-wrap items-center justify-end gap-2">
          <NextButton
            faded
            slate
            :label="t('WA_TEMPLATES.FORM.CANCEL')"
            @click="leave"
          />
          <NextButton
            :is-loading="submitting"
            :disabled="!canSubmit"
            :label="t('WA_TEMPLATES.FORM.SUBMIT')"
            @click="submit"
          />
        </div>
      </div>
    </div>
  </div>
</template>
